/**
 * ai-assistant — server response shapes (mirror of server/src/modules/ai-assistant/*). The server
 * returns the standard { success, data } envelope; the api/useApiQuery layer unwraps `data`, so these
 * types describe the UNWRAPPED payload. BigInt ids serialize to JSON numbers/strings, so every id is
 * `string | number` and normalized with `idStr()` before use as a key/param.
 *
 * Only the fields the screens read are typed; the server may carry more keys (forward-compatible).
 */

/** A normalized id — the wire carries either; callers compare/scope on the string form. */
export type Id = string | number;

/** Coerce a wire id to a stable string (BigInt-safe; never produces "[object Object]"). */
export function idStr(id: Id | null | undefined): string {
  return id == null ? "" : String(id);
}

// ── Conversation ──────────────────────────────────────────────────────────────────

/** Result of POST /assistant/sessions — a fresh helpdesk session. */
export interface OpenSessionResult {
  session_id: Id;
}

/**
 * The tagged, NEVER-throws answer of POST /assistant/sessions/:id/messages (conversation.repondre).
 *  - ok          → a grounded operational answer in `texte`, with `chunks_utilises` citing the SOP titles.
 *  - abstenu     → a clinical request was deterministically refused (REFUS_CLINIQUE in `texte`).
 *  - disabled    → no AI key/SDK on this install → "Assistant indisponible pour le moment." (graceful).
 *  - error       → a non-blocking failure → "Assistant indisponible." (never in an operational path).
 */
export type AnswerStatus = "ok" | "abstenu" | "disabled" | "error";

export interface Answer {
  status: AnswerStatus;
  /** The assistant reply (or the governed refusal / the polite indisponible message). */
  texte: string;
  /** The config-governed bilingual non-clinical disclaimer (always present). */
  disclaimer: string;
  /** The titres of the RBAC-scoped chunks the answer was grounded on (the citations). */
  chunks_utilises: string[];
}

// ── Instructions.md (AAS-5) ─────────────────────────────────────────────────────────

/**
 * The active per-lab instructions.md (GET /assistant/instructions). Append-only versioned: each
 * publish creates version+1 actif=true and demotes the prior. `null` when none has ever been
 * published. The server row carries snake_case fields; only what the editor reads is typed here.
 */
export interface Instructions {
  id: Id;
  contenu_md: string;
  version: number;
  actif?: boolean;
  redige_par?: Id | null;
  created_at?: string;
  version_precedente_id?: Id | null;
}

/** Result of PUT /assistant/instructions — the freshly published version. */
export interface PublishInstructionsResult {
  instructions_id: Id;
  version: number;
}

// ── Local conversation model (client-held; the server has no GET-messages route) ──────

export type Role = "utilisateur" | "assistant";

/** One turn rendered in the conversation surface. The user turn is local; the assistant turn carries
 * the answer envelope so the screen can render its status + citations + disclaimer. */
export interface Turn {
  /** Stable client-side key. */
  key: string;
  role: Role;
  texte: string;
  /** Present only on an assistant turn. */
  status?: AnswerStatus;
  chunks?: string[];
  disclaimer?: string;
}
